// mds-dojo — dijit/form/NumberTextBox — FAITHFUL port of references/dijit/form/NumberTextBox.js.
// Codemod pipeline; declare from the faithful runtime. Adaptations:
//  - `dojo/number`: the canonical mds dojo/number is a slim en-only module (format/parse/round
//    only). The regexp generators (number.regexp / number._realNumberRegexp) it lacks are ported
//    faithfully in ./_numberRegexp and composed into the local `number` object below. See the
//    LOUD flag in ./_numberRegexp for the (locale-only) behavioral note.
//  - `dojo/i18n` (getLocalization/normalizeLocale) for getDecimalInfo() replaced by the inlined
//    en/root CLDR number bundle below — the mds number stack has no i18n loader (en-only).
// A TextBox for entering numbers, with formatting and range checking.
import declare from '../../dojo/_base/declare-runtime'; // declare
import lang from '../../dojo/_base/lang'; // lang.hitch lang.mixin
import string from '../../dojo/string'; // string.rep
import baseNumber from '../../dojo/number'; // number.format number.parse number.round
import numberRegexp from './_numberRegexp'; // number.regexp number._realNumberRegexp
import RangeBoundTextBox from './RangeBoundTextBox';

// Composed dojo/number: slim mds format/parse/round + faithfully-ported regexp generators.
var number: any = {
	format: baseNumber.format,
	parse: baseNumber.parse,
	round: baseNumber.round,
	regexp: numberRegexp.regexp,
	_realNumberRegexp: numberRegexp._realNumberRegexp
};

// Inlined dojo/cldr/nls/number en bundle (replaces i18n.getLocalization for getDecimalInfo).
var numberBundle: any = {
	decimalFormat: "#,##0.###",
	scientificFormat: "#E0",
	percentFormat: "#,##0%",
	currencyFormat: "¤#,##0.00;(¤#,##0.00)",
	decimal: "."
};

// A private helper function to determine decimal information
// Returns an object with "sep" and "places" properties
var getDecimalInfo = function (constraints: any) {
	var constraints = constraints || {},
		bundle = numberBundle,
		pattern = constraints.pattern ? constraints.pattern : bundle[(constraints.type || "decimal") + "Format"];

	// The number of places in the constraint can be specified in several ways,
	// the resolution order is:
	//
	// 1. If constraints.places is a number, use that
	// 2. If constraints.places is a string, which specifies a range, use the range max (e.g. 0,4)
	// 3. If a pattern is specified, use the implicit number of places in the pattern.
	// 4. If neither constraints.pattern or constraints.places is specified, use the locale default pattern
	var places;
	if (typeof constraints.places == "number") {
		places = constraints.places;
	} else if (typeof constraints.places === "string" && constraints.places.length > 0) {
		places = constraints.places.replace(/.*,/, "");
	} else {
		places = (pattern.indexOf(".") != -1 ? pattern.split(".")[1].replace(/[^#0]/g, "").length : 0);
	}

	return { sep: bundle.decimal, places: places };
};

var NumberTextBoxMixin: any = declare("dijit.form.NumberTextBoxMixin", null, {
	// summary:
	//		A mixin for all number textboxes
	// tags:
	//		protected

	// Override ValidationTextBox.pattern.... we use a reg-ex generating function rather
	// than a straight regexp to deal with locale (plus formatting options too?)
	pattern: function (constraints: any) {
		// if focused, accept either currency data or NumberTextBox format
		return '(' + (this.focused && this.editOptions ? this._regExpGenerator(lang.delegate(constraints, this.editOptions)) + '|' : '')
			+ this._regExpGenerator(constraints) + ')';
	},

	// value: Number
	//		The value of this NumberTextBox as a Javascript Number (i.e., not a String).
	value: NaN,

	// editOptions: [protected] Object
	//		Properties to mix into constraints when the value is being edited.
	editOptions: { pattern: '#.######' },

	_formatter: number.format,

	_regExpGenerator: number.regexp,

	// _decimalInfo: Object
	//		An object containing decimal related properties relevant to this TextBox.
	_decimalInfo: getDecimalInfo(undefined),

	postMixInProperties: function () {
		this.inherited(arguments);
		this._set("type", "text"); // in case type="number" was specified which messes up parse/format
	},

	_setConstraintsAttr: function (/*Object*/ constraints: any) {
		var places = typeof constraints.places == "number" ? constraints.places : 0;
		if (places) { places++; } // decimal rounding errors take away another digit of precision
		if (typeof constraints.max != "number") {
			constraints.max = 9 * Math.pow(10, 15 - places);
		}
		if (typeof constraints.min != "number") {
			constraints.min = -9 * Math.pow(10, 15 - places);
		}
		this.inherited(arguments, [constraints]);
		if (this.focusNode && this.focusNode.value && !isNaN(this.value)) {
			this.set('value', this.value);
		}
		// Capture decimal information based on the constraint locale and pattern.
		this._decimalInfo = getDecimalInfo(constraints);
	},

	_onFocus: function (/*String*/ by: any) {
		if (this.disabled || this.readOnly) { return; }
		var val = this.get('value');
		if (typeof val == "number" && !isNaN(val)) {
			var formattedValue = this.format(val, this.constraints);
			if (formattedValue !== undefined) {
				this.textbox.value = formattedValue;
				// when NumberTextBox or descendants (i.e. CurrencyTextBox) format textbox.value when focused
				// all browsers except Chrome will select textbox contents when tabbed to by keyboard
				// force selection if not focused by mouse
				if (by !== "mouse") {
					this.textbox.select();
				}
			}
		}
		this.inherited(arguments);
	},

	format: function (/*Number*/ value: any, /*number.__FormatOptions*/ constraints: any) {
		// summary:
		//		Formats the value as a Number, according to constraints.
		// tags:
		//		protected

		var formattedValue = String(value);
		if (typeof value != "number") { return formattedValue; }
		if (isNaN(value)) { return ""; }
		// check for exponential notation that dojo/number.format() chokes on
		if (!("rangeCheck" in this && this.rangeCheck(value, constraints)) && constraints.exponent !== false && /\de[-+]?\d/i.test(formattedValue)) {
			return formattedValue;
		}
		if (this.editOptions && this.focused) {
			constraints = lang.mixin({}, constraints, this.editOptions);
		}
		return this._formatter(value, constraints);
	},

	_parser: number.parse,

	parse: function (/*String*/ value: any, /*number.__FormatOptions*/ constraints: any) {
		// summary:
		//		Replaceable function to convert a formatted string to a number value
		// tags:
		//		protected extension
		var parserOptions = lang.mixin({}, constraints, (this.editOptions && this.focused) ? this.editOptions : {})
		if (this.focused && parserOptions.places != null /* or undefined */) {
			var places = parserOptions.places;
			var maxPlaces = typeof places === "number" ? places : Number(places.split(",").pop()); // handle number and range
			parserOptions.places = "0," + maxPlaces;
		}
		var v = this._parser(value, parserOptions);
		if (this.editOptions && this.focused && isNaN(v)) {
			v = this._parser(value, constraints); // parse w/o editOptions: not technically needed but is nice for the user
		}
		return v;
	},

	_getDisplayedValueAttr: function () {
		var v = this.inherited(arguments);
		return isNaN(v) ? this.textbox.value : v;
	},

	filter: function (/*Number*/ value: any) {
		// summary:
		//		This is called with both the display value (string), and the actual value (a number).
		//		When called with the actual value it does corrections so that '' etc. are represented as NaN.
		//		Otherwise it dispatches to the superclass's filter() method.
		if (value == null  /* or undefined */ || typeof value == "string" && value == '') {
			return NaN;
		} else if (typeof value == "number" && !isNaN(value) && value != 0) {
			value = number.round(value, this._decimalInfo.places);
		}
		return this.inherited(arguments, [value]);
	},

	serialize: function (/*Number*/ value: any, /*Object?*/ options: any) {
		// summary:
		//		Convert value (a Number) into a canonical string (ie, how the number literal is written in javascript/java/C/etc.)
		// tags:
		//		protected
		return (typeof value != "number" || isNaN(value)) ? '' : this.inherited(arguments);
	},

	_setBlurValue: function () {
		var val = lang.hitch(lang.delegate(this, { focused: true }), "get")('value'); // parse with editOptions
		this._setValueAttr(val, true);
	},

	_setValueAttr: function (/*Number*/ value: any, /*Boolean?*/ priorityChange?: any, /*String?*/ formattedValue?: any) {
		// summary:
		//		Hook so set('value', ...) works.
		if (value !== undefined && formattedValue === undefined) {
			formattedValue = String(value);
			if (typeof value == "number") {
				if (isNaN(value)) { formattedValue = '' }
				// check for exponential notation that number.format chokes on
				else if (("rangeCheck" in this && this.rangeCheck(value, this.constraints)) || this.constraints.exponent === false || !/\de[-+]?\d/i.test(formattedValue)) {
					formattedValue = undefined; // lets format compute a real string value
				}
			} else if (!value) { // 0 processed in if branch above, ''|null|undefined flows through here
				formattedValue = '';
				value = NaN;
			} else { // non-numeric values
				value = undefined;
			}
		}
		this.inherited(arguments, [value, priorityChange, formattedValue]);
	},

	_getValueAttr: function () {
		// summary:
		//		Hook so get('value') works.
		//		Returns Number, NaN for '', or undefined for unparseable text
		var v = this.inherited(arguments); // returns Number for all values accepted by parse() or NaN for all other displayed values

		// If the displayed value of the textbox is gibberish (ex: "hello world"), this.inherited() above
		// returns NaN; this if() branch converts the return value to undefined.
		if (isNaN(v) && this.textbox.value !== '') {
			if (this.constraints.exponent !== false && /\de[-+]?\d/i.test(this.textbox.value) && (new RegExp("^" + number._realNumberRegexp(lang.delegate(this.constraints)) + "$").test(this.textbox.value))) {	// check for exponential notation that parse() rejected (erroneously?)
				var n = Number(this.textbox.value);
				return isNaN(n) ? undefined : n; // return exponential Number or undefined for random text (may not be possible to do with the above RegExp check)
			} else {
				return undefined; // gibberish
			}
		} else {
			return v; // Number or NaN for ''
		}
	},

	isValid: function (/*Boolean*/ isFocused: any) {
		// Overrides dijit/form/RangeBoundTextBox.isValid() to check that the editing-mode value is valid since
		// it may not be formatted according to the regExp validation rules
		if (!this.focused || this._isEmpty(this.textbox.value)) {
			return this.inherited(arguments);
		} else {
			var v = this.get('value');
			if (!isNaN(v) && this.rangeCheck(v, this.constraints)) {
				if (this.constraints.exponent !== false && /\de[-+]?\d/i.test(this.textbox.value)) { // exponential, parse doesn't like it
					return true; // valid exponential number in range
				} else {
					return this.inherited(arguments);
				}
			} else {
				return false;
			}
		}
	},

	_isValidSubset: function () {
		// Overrides dijit/form/ValidationTextBox._isValidSubset()
		//
		// See ticket #17923
		var hasMinConstraint = (typeof this.constraints.min == "number"),
			hasMaxConstraint = (typeof this.constraints.max == "number"),
			curVal = this.get('value');

		// If there is no parsable number, or there are no min or max bounds, then we can safely
		// skip all remaining checks
		if (isNaN(curVal) || (!hasMinConstraint && !hasMaxConstraint)) {
			return this.inherited(arguments);
		}

		var integerDigits = curVal | 0,
			valNegative = curVal < 0,
			// Check if the current number has a decimal based on its locale
			hasDecimal = this.textbox.value.indexOf(this._decimalInfo.sep) != -1,
			// Determine the max digits based on the textbox length.
			maxDigits = this.maxLength || 20,
			// Determine the remaining digits, based on the max digits
			remainingDigitsCount = maxDigits - this.textbox.value.length,
			// avoid approximation issues by capturing the decimal portion of the value as the user-entered string
			fractionalDigitStr = hasDecimal ? this.textbox.value.split(this._decimalInfo.sep)[1].replace(/[^0-9]/g, "") : "";

		// Create a normalized value string in the form of #.###
		var normalizedValueStr = hasDecimal ? integerDigits + "." + fractionalDigitStr : integerDigits + "";

		var ninePaddingStr = string.rep("9", remainingDigitsCount),
			minPossibleValue = curVal,
			maxPossibleValue = curVal;
		if (valNegative) {
			minPossibleValue = Number(normalizedValueStr + ninePaddingStr);
		} else {
			maxPossibleValue = Number(normalizedValueStr + ninePaddingStr);
		}

		return !((hasMinConstraint && maxPossibleValue < this.constraints.min)
			|| (hasMaxConstraint && minPossibleValue > this.constraints.max));
	}
});

var NumberTextBox: any = declare("dijit.form.NumberTextBox", [RangeBoundTextBox, NumberTextBoxMixin], {
	// summary:
	//		A TextBox for entering numbers, with formatting and range checking

	baseClass: "dijitTextBox dijitNumberTextBox"
});

NumberTextBox.Mixin = NumberTextBoxMixin;	// for monkey patching

export default NumberTextBox;
